/**
 * Variant Boxes - Custom Variant Selector
 * Manages variant selection with quantity controls for 4+ option
 */

class VariantBoxes {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.selectedVariantId = null;
    this.bulkVariantId = null; // 4+ varyant ID
    this.boxes = container.querySelectorAll('.variant-box');
    this.hiddenInput = container.querySelector('.variant-boxes-selected');
    this.quantityInput = null;
    
    this.init();
  }

  init() {
    // Get quantity input from the page
    this.quantityInput = document.querySelector('input[name="quantity"]');
    this.quantityContainer = document.querySelector('#Quantity-Form-' + this.container.closest('.product__info-container')?.closest('section')?.id?.replace('ProductInfo-', ''));
    
    // Find 4+ variant (hidden, not in boxes)
    this.findBulkVariant();
    
    // Setup box click handlers
    this.boxes.forEach(box => {
      box.addEventListener('click', (e) => {
        if (!e.target.classList.contains('variant-box-remove')) {
          this.selectBox(box);
        }
      });

      // Remove button handler
      const removeBtn = box.querySelector('.variant-box-remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.deselectBox(box);
        });
      }
    });

    // Watch quantity changes
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => {
        this.handleQuantityChange();
        this.updateQuantityLabel();
      });
      this.quantityInput.addEventListener('input', () => {
        this.handleQuantityChange();
        this.updateQuantityLabel();
      });
    }

    // Initial selection from URL or first variant
    this.initializeSelection();
    
    // Initially disable quantity if not 3+
    this.updateQuantityState();
  }
  
  findBulkVariant() {
    // Find the 4+ variant from product data
    const productData = this.getProductData();
    if (!productData || !productData.variants) return;
    
    // Look for variant with "4+" in options
    const bulkVariant = productData.variants.find(v => {
      return v.options.some(opt => 
        opt === '4+' || 
        opt.toLowerCase().includes('4+') ||
        opt.toLowerCase().includes('4 +')
      );
    });
    
    if (bulkVariant) {
      this.bulkVariantId = bulkVariant.id;
      console.log('Found 4+ variant:', this.bulkVariantId);
    }
  }

  getProductData() {
    // Try to get product data from the page
    const productJson = document.querySelector(`script[type="application/json"][id*="product"]`);
    if (productJson) {
      try {
        return JSON.parse(productJson.textContent);
      } catch (e) {
        console.error('Failed to parse product JSON', e);
      }
    }
    return null;
  }

  selectBox(box) {
    if (!box) {
      console.error('selectBox: No box provided');
      return;
    }
    
    console.log('=== SELECT BOX ===');
    console.log('Box variant ID:', box.dataset.variantId);
    console.log('Box option:', box.dataset.variantOption);
    
    // Deselect all other boxes
    this.boxes.forEach(b => {
      b.classList.remove('selected');
      const removeBtn = b.querySelector('.variant-box-remove');
      if (removeBtn) removeBtn.style.display = 'none';
    });

    // Select this box
    box.classList.add('selected');
    const oldVariantId = this.selectedVariantId;
    this.selectedVariantId = box.dataset.variantId;
    const selectedOption = box.dataset.variantOption;
    
    console.log('New selected variant ID:', this.selectedVariantId);
    
    // Show remove button
    const removeBtn = box.querySelector('.variant-box-remove');
    if (removeBtn) removeBtn.style.display = 'block';

    // Update hidden input immediately
    this.hiddenInput.value = this.selectedVariantId;

    // Reset quantity to 1 when selecting a box
    this.updateQuantityState(selectedOption);

    // IMMEDIATELY update form with selected variant
    this.updateProductForm();

    // Check if we need to switch to bulk variant (will call updateProductForm again if needed)
    this.handleQuantityChange();

    // Trigger variant change event
    if (oldVariantId !== this.selectedVariantId) {
      this.triggerVariantChange();
    }
    
    console.log('=== SELECT BOX COMPLETE ===');
  }

  deselectBox(box) {
    box.classList.remove('selected');
    const removeBtn = box.querySelector('.variant-box-remove');
    if (removeBtn) removeBtn.style.display = 'none';
    
    this.selectedVariantId = null;
    this.hiddenInput.value = '';
    
    // Reset quantity to 1
    if (this.quantityInput) {
      this.quantityInput.value = '1';
    }
    
    // Update label
    const quantityLabel = this.quantityContainer?.querySelector('.quantity__label span[aria-hidden="true"]');
    if (quantityLabel) {
      quantityLabel.textContent = 'Adet (Sepette: 1)';
    }
  }

  updateQuantityState(selectedOption = null) {
    if (!this.quantityContainer) {
      // Try to find it again if not found initially
      const sectionId = document.querySelector('product-info')?.dataset.section;
      if (sectionId) {
        this.quantityContainer = document.querySelector('#Quantity-Form-' + sectionId);
      }
    }

    if (!this.quantityContainer) return;

    // Get quantity label
    const quantityLabel = this.quantityContainer.querySelector('.quantity__label');

    // Always enable quantity
    this.quantityContainer.style.opacity = '1';
    this.quantityContainer.style.pointerEvents = 'auto';
    
    if (this.quantityInput) {
      this.quantityInput.disabled = false;
      this.quantityInput.style.opacity = '1';
      
      // ALWAYS set quantity to 1 when changing boxes
      this.quantityInput.value = '1';
      this.quantityInput.min = '1';
      this.quantityInput.setAttribute('data-min', '1');
      
      // Update label
      if (quantityLabel) {
        const labelText = quantityLabel.querySelector('span[aria-hidden="true"]');
        if (labelText) {
          labelText.textContent = 'Adet (Sepette: 1)';
        }
      }
    }
    
    // Enable all buttons
    const buttons = this.quantityContainer.querySelectorAll('button');
    buttons.forEach(btn => {
      btn.disabled = false;
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
    });
  }

  handleQuantityChange() {
    if (!this.quantityInput || !this.selectedVariantId) return;

    const quantity = parseInt(this.quantityInput.value) || 1;
    
    // SIMPLE LOGIC:
    // If quantity > 3 AND we have 4+ variant → use 4+ variant with that quantity
    // Otherwise → use selected group variant with quantity=1
    
    if (quantity > 3 && this.bulkVariantId) {
      // Use 4+ variant
      this.hiddenInput.value = this.bulkVariantId;
      console.log(`Quantity ${quantity} > 3: Using 4+ variant (ID: ${this.bulkVariantId}) × ${quantity}`);
    } else {
      // Use selected variant (grup 1, 2, or 3) - always 1 piece
      this.hiddenInput.value = this.selectedVariantId;
      console.log(`Quantity ${quantity} <= 3: Using selected grup variant (ID: ${this.selectedVariantId}) × 1`);
    }

    // Update the form
    this.updateProductForm();
  }

  updateHiddenInput() {
    if (this.hiddenInput) {
      this.hiddenInput.value = this.selectedVariantId || '';
    }
  }

  updateProductForm() {
    // Update ALL possible variant inputs in the form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) {
      console.error('Product form not found!');
      return;
    }
    
    // Find all variant inputs
    const variantInputs = productForm.querySelectorAll('input[name="id"], .product-variant-id');
    const quantityInputInForm = productForm.querySelector('input[name="quantity"]');
    
    if (!this.hiddenInput.value) {
      console.error('No variant selected!');
      return;
    }
    
    console.log('--- UPDATE PRODUCT FORM ---');
    console.log('Selected variant ID:', this.hiddenInput.value);
    console.log('Is 4+ variant:', this.hiddenInput.value === String(this.bulkVariantId));
    
    // Update ALL variant inputs
    variantInputs.forEach((input, index) => {
      console.log(`Updating variant input #${index}: ${input.value} → ${this.hiddenInput.value}`);
      input.value = this.hiddenInput.value;
      input.disabled = false;
    });
    
    // Fix quantity based on variant type
    const quantity = parseInt(this.quantityInput?.value || 1);
    const isUsing4Plus = this.hiddenInput.value === String(this.bulkVariantId);
    
    if (quantityInputInForm) {
      const oldQty = quantityInputInForm.value;
      if (isUsing4Plus) {
        // Using 4+ variant: use actual quantity
        quantityInputInForm.value = quantity;
        console.log(`Quantity set to ${quantity} (4+ variant)`);
      } else {
        // Using grup 1/2/3: always 1
        quantityInputInForm.value = '1';
        console.log(`Quantity forced to 1 (grup variant)`);
      }
    }
    
    // Trigger change event for Shopify
    variantInputs.forEach(input => {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    
    // Update URL
    if (window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', this.hiddenInput.value);
      window.history.replaceState({}, '', url);
    }
    
    console.log('--- FORM UPDATE COMPLETE ---');
  }

  triggerVariantChange() {
    console.log('=== TRIGGER VARIANT CHANGE ===');
    console.log('Variant ID:', this.selectedVariantId || this.hiddenInput.value);
    
    // Update product form first (MOST IMPORTANT)
    this.updateProductForm();
    
    // Update price display
    this.updatePriceDisplay();
    
    // Force update product-form web component
    const productFormElement = document.querySelector('product-form');
    if (productFormElement) {
      console.log('Forcing product-form component update');
      
      // Update the form's internal state
      const formElement = productFormElement.querySelector('form');
      if (formElement) {
        const variantInput = formElement.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = this.hiddenInput.value;
          console.log('Updated product-form input to:', this.hiddenInput.value);
        }
      }
      
      // Trigger component update if method exists
      if (typeof productFormElement.onVariantChange === 'function') {
        productFormElement.onVariantChange();
      }
    }
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('variantBoxChange', {
      detail: {
        variantId: this.selectedVariantId,
        container: this.container
      }
    }));
    
    console.log('=== VARIANT CHANGE COMPLETE ===');
  }

  updatePriceDisplay() {
    if (!this.selectedVariantId) return;

    // Get the selected variant data
    const productData = this.getProductData();
    if (!productData || !productData.variants) return;

    const selectedVariant = productData.variants.find(v => v.id == this.selectedVariantId);
    if (!selectedVariant) return;

    // Format price in Turkish Lira
    const formatPrice = (cents) => {
      const lira = cents / 100;
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(lira);
    };

    // Update main price container
    const priceContainer = document.querySelector('#price-' + this.container.closest('section')?.id?.replace('ProductInfo-', ''));
    if (priceContainer) {
      const priceItem = priceContainer.querySelector('.price-item--regular');
      if (priceItem) {
        priceItem.textContent = formatPrice(selectedVariant.price);
      }

      // Update compare at price
      const compareItem = priceContainer.querySelector('.price-item--sale');
      const priceWrapper = priceContainer.querySelector('.price');
      
      if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
        if (compareItem) {
          compareItem.textContent = formatPrice(selectedVariant.compare_at_price);
        }
        if (priceWrapper && !priceWrapper.classList.contains('price--on-sale')) {
          priceWrapper.classList.add('price--on-sale');
        }
      } else {
        if (priceWrapper && priceWrapper.classList.contains('price--on-sale')) {
          priceWrapper.classList.remove('price--on-sale');
        }
      }
    }

    // Fallback to any price element
    const priceRegular = document.querySelector('.price__regular .price-item--regular');
    if (priceRegular) {
      priceRegular.textContent = formatPrice(selectedVariant.price);
    }
  }

  updateQuantityLabel() {
    if (!this.quantityInput || !this.quantityContainer) return;
    
    const quantityLabel = this.quantityContainer.querySelector('.quantity__label');
    if (quantityLabel) {
      const labelText = quantityLabel.querySelector('span[aria-hidden="true"]');
      if (labelText && this.quantityInput.value) {
        labelText.textContent = `Adet (Sepette: ${this.quantityInput.value})`;
      }
    }
  }

  initializeSelection() {
    console.log('=== INITIALIZE SELECTION ===');
    
    // Check URL for variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    
    if (urlVariantId) {
      console.log('URL variant:', urlVariantId);
      const box = this.container.querySelector(`[data-variant-id="${urlVariantId}"]`);
      if (box) {
        console.log('Found box for URL variant');
        this.selectBox(box);
        return;
      }
    }

    // Otherwise select first box by default
    if (this.boxes.length > 0) {
      console.log('Selecting first box by default');
      this.selectBox(this.boxes[0]);
      
      // Force update form immediately after selection
      setTimeout(() => {
        console.log('Force updating form after init');
        this.updateProductForm();
      }, 100);
    }
  }
}

// Store instances
const variantBoxesInstances = new Map();

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.variant-boxes-container');
  containers.forEach(container => {
    const instance = new VariantBoxes(container);
    variantBoxesInstances.set(container, instance);
  });
});

// Re-initialize on Shopify section updates (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    const containers = document.querySelectorAll('.variant-boxes-container');
    containers.forEach(container => {
      // Remove old instance if exists
      if (variantBoxesInstances.has(container)) {
        variantBoxesInstances.delete(container);
      }
      const instance = new VariantBoxes(container);
      variantBoxesInstances.set(container, instance);
    });
  });
}

// Listen to external variant changes (from other variant pickers)
document.addEventListener('variant:change', (event) => {
  const variantId = event.detail?.variant;
  if (!variantId) return;
  
  // Update all variant boxes to reflect the change
  variantBoxesInstances.forEach((instance, container) => {
    const box = container.querySelector(`[data-variant-id="${variantId}"]`);
    if (box && !box.classList.contains('selected')) {
      instance.selectBox(box);
    }
  });
});
